import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Check, ShieldCheck, Gift, Award, AlertTriangle, ExternalLink, Wallet,
  Zap, FlaskConical, Activity, Crown, Truck, Droplets, Percent, BadgeDollarSign,
  ChevronDown, Sparkles, Info,
} from 'lucide-react';
import {
  API, BHN_STATIC_URL, DISCLOSURE, PROMO_DISCLOSURE, TIERS, VALUE_MATH, COMPARISON,
  PEPTIDE_CATEGORIES, FAQS, JSONLD, trackEvent, getSessionId, SECTION_ORDER,
} from './peptideContent';

const H = { fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '0.04em' };

const TIER_STYLE = {
  network: {
    accent: 'text-white', border: 'border-white/15', glow: '',
    badge: 'bg-white/10 text-zinc-300 border border-white/15',
    btn: 'border border-white/30 text-white hover:bg-white hover:text-black',
    bar: 'bg-zinc-400', check: 'text-zinc-300',
    cardBg: 'linear-gradient(180deg, #161619 0%, #0D0D10 100%)',
  },
  pro: {
    accent: 'text-[#00F5D4]', border: 'border-[#00F5D4]/40', glow: 'shadow-[0_0_35px_rgba(0,245,212,0.15)]',
    badge: 'bg-gradient-to-r from-[#00F5D4] to-[#70E000] text-black font-bold',
    btn: 'bg-gradient-to-r from-[#00F5D4] to-[#70E000] text-black font-bold hover:brightness-110',
    bar: 'bg-gradient-to-r from-[#00F5D4] to-[#70E000]', check: 'text-[#00F5D4]',
    cardBg: 'linear-gradient(180deg, #111D1C 0%, #0E1414 100%)',
  },
  elite: {
    accent: 'text-[#FBBF24]', border: 'border-[#F59E0B]/45', glow: 'shadow-[0_0_35px_rgba(245,158,11,0.15)]',
    badge: 'bg-[#F59E0B]/10 text-[#FBBF24] border border-[#F59E0B]/40',
    btn: 'border border-[#F59E0B] text-[#FBBF24] hover:bg-[#F59E0B] hover:text-black font-bold',
    bar: 'bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]', check: 'text-[#FBBF24]',
    cardBg: 'linear-gradient(180deg, #1F1910 0%, #0E0C09 100%)',
  },
};

const useMeta = () => {
  useEffect(() => {
    document.title = 'Peptide Subscription Membership: Tiers, Pricing & Provider-Guided Access (2026) | NinjAthlete';
    const setMeta = (name, content, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(prop ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const desc = 'Compare provider-guided peptide therapy membership tiers: Network $99/mo, Pro $199/mo ($250 credit + 30% off), Elite $299/mo ($400 credit + 40% off). Licensed pharmacies, cancel anytime, free NinjAthlete merch.';
    setMeta('description', desc);
    setMeta('og:title', 'Peptide Subscription Membership — Get More Credit Than You Pay', true);
    setMeta('og:description', desc, true);
    setMeta('og:url', 'https://ninjathlete.com/peptide-subscription', true);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://ninjathlete.com/peptide-subscription');
    let ld = document.getElementById('peptide-jsonld');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.id = 'peptide-jsonld';
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(JSONLD);
    return () => { const s = document.getElementById('peptide-jsonld'); if (s) s.remove(); };
  }, []);
};

const useScrollDepth = () => {
  useEffect(() => {
    let maxIdx = 0;
    let sent = false;
    const mountedAt = Date.now();
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          const idx = SECTION_ORDER.indexOf(e.target.dataset.section);
          if (idx > maxIdx) maxIdx = idx;
        }
      });
    }, { threshold: 0.15 });
    document.querySelectorAll('[data-section]').forEach((el) => obs.observe(el));
    const send = () => {
      if (sent || Date.now() - mountedAt < 1500) return;
      sent = true;
      const payload = JSON.stringify({ event: 'NA_scroll_depth', placement: SECTION_ORDER[maxIdx], session_id: getSessionId() });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/api/referral/track`, new Blob([payload], { type: 'application/json' }));
      } else {
        fetch(`${API}/api/referral/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
      }
    };
    const onHide = () => { if (document.visibilityState === 'hidden') send(); };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', send);
    return () => {
      obs.disconnect();
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', send);
      send();
    };
  }, []);
};

const CtaButton = ({ placement, urls, label, className, testid }) => (
  <a
    href={urls[placement] || BHN_STATIC_URL(placement)}
    target="_blank" rel="noopener noreferrer sponsored"
    title={DISCLOSURE}
    onClick={() => trackEvent('NA_membership_cta_click', placement)}
    className={`inline-flex items-center justify-center gap-2 px-6 py-3.5 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${className}`}
    data-testid={testid}
  >
    {label} <ArrowRight className="w-3.5 h-3.5" />
  </a>
);

const ScarcityMeter = ({ left, total, barClass }) => (
  <div data-testid="scarcity-meter">
    <div className="flex items-center justify-between mb-1.5">
      <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
        <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /> {left} of {total} spots left
      </span>
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Limited</span>
    </div>
    <div className="h-1 bg-white/10">
      <div className={`h-full ${barClass}`} style={{ width: `${Math.round((1 - left / total) * 100) || 6}%`, minWidth: '6%' }} />
    </div>
  </div>
);

const Section = ({ section, title, eyebrow, children, testid, className = '' }) => (
  <section data-section={section} className={`mb-20 ${className}`} data-testid={testid}>
    {eyebrow && <p className="text-[#C7FA3E] text-[11px] uppercase tracking-[0.3em] mb-2">{eyebrow}</p>}
    {title && <h2 className="text-white text-3xl sm:text-4xl mb-5" style={H}>{title}</h2>}
    {children}
  </section>
);

const TierCard = ({ tier, urls }) => {
  const s = TIER_STYLE[tier.key];
  return (
    <div className={`relative border ${s.border} ${s.glow} p-7 flex flex-col`} style={{ background: s.cardBg }} data-testid={`peptide-tier-${tier.key}`}>
      <span className={`absolute -top-3 left-6 px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${s.badge}`}>{tier.badge}</span>
      <h3 className={`text-2xl mt-3 ${s.accent}`} style={H}>{tier.name}</h3>
      <div className="flex items-baseline gap-2 mt-1 mb-4">
        <span className={`text-5xl font-bold ${s.accent}`}>${tier.monthly}</span>
        <span className="text-zinc-500 text-sm">/ month</span>
      </div>
      <div className={`border-l-2 ${tier.key === 'network' ? 'border-white/40' : tier.key === 'pro' ? 'border-[#00F5D4]' : 'border-[#F59E0B]'} bg-white/[0.03] px-4 py-3 mb-4`}>
        <p className={`text-xl font-bold ${s.accent}`}>GET ${tier.credit}</p>
        <p className="text-zinc-500 text-[10px] uppercase tracking-[0.2em]">Monthly store credit</p>
        <p className="text-white text-xs font-bold mt-1.5">+ {tier.discount}% OFF ALL ORDERS{tier.key !== 'network' ? ' + FREE SHIPPING' : ''}</p>
      </div>
      <p className="text-zinc-400 text-xs mb-1">{tier.tagline}</p>
      {tier.highlight && <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${s.accent}`}>{tier.highlight}</p>}
      <ul className="space-y-2.5 my-4 flex-1">
        {tier.includes.map((i) => (
          <li key={i} className="flex items-start gap-2.5 text-zinc-300 text-[13px] leading-snug">
            <Check className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${s.check}`} /> {i}
          </li>
        ))}
      </ul>
      <p className="text-zinc-500 text-[11px] uppercase tracking-wider mb-4">{tier.mathLine}</p>
      <div className="mb-5"><ScarcityMeter left={tier.spotsLeft} total={tier.spotsTotal} barClass={s.bar} /></div>
      <CtaButton
        placement={tier.placement} urls={urls}
        label={tier.key === 'pro' ? `Unlock Pro — $${tier.monthly}/mo` : `Join ${tier.name} — $${tier.monthly}/mo`}
        className={`w-full ${s.btn}`}
        testid={`peptide-cta-${tier.placement}`}
      />
    </div>
  );
};

const FaqItem = ({ f, i, open, onToggle }) => (
  <div className="border border-white/10 bg-[#121214]" data-testid={`peptide-faq-${i}`}>
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`peptide-faq-toggle-${i}`}>
      <h3 className="text-white text-sm font-bold">{f.q}</h3>
      <ChevronDown className={`w-4 h-4 text-[#C7FA3E] flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <p className="px-5 pb-5 text-zinc-400 text-[13px] leading-relaxed">{f.a}</p>}
  </div>
);

export default function PeptideSubscriptionPage() {
  useMeta();
  useScrollDepth();
  const [urls, setUrls] = useState({});
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    trackEvent('NA_subscription_page_view');
    try {
      const cached = JSON.parse(sessionStorage.getItem('bhn_ref_urls') || 'null');
      if (cached?.urls && cached.expires_at * 1000 > Date.now() + 86400000) { setUrls(cached.urls); return; }
    } catch (e) { /* ignore */ }
    fetch(`${API}/api/referral/mint`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
    }).then((r) => r.json()).then((d) => {
      setUrls(d.urls || {});
      try { sessionStorage.setItem('bhn_ref_urls', JSON.stringify(d)); } catch (e) { /* ignore */ }
    }).catch(() => {});
  }, []);

  const scrollToTiers = () => document.getElementById('membership-tiers')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="peptide-subscription-page">
      {/* ── Hero ── */}
      <header data-section="hero" className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(circle at 50% 0%, rgba(199,250,62,0.07) 0%, transparent 60%)' }} />
        <div className="relative max-w-6xl mx-auto px-5 pt-28 pb-14">
          <nav className="text-zinc-600 text-xs mb-6" aria-label="Breadcrumb" data-testid="peptide-breadcrumb">
            <Link to="/" className="hover:text-zinc-400">Home</Link> <span className="mx-1">/</span>
            <Link to="/peptide-cheat-sheet" className="hover:text-zinc-400">Peptides</Link> <span className="mx-1">/</span>
            <span className="text-zinc-400">Subscription Membership</span>
          </nav>
          <span className="inline-flex items-center gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/[0.06] text-[#FBBF24] text-[10px] uppercase tracking-[0.3em] px-4 py-2 mb-6" data-testid="peptide-hero-pill">
            <Sparkles className="w-3.5 h-3.5" /> 2026 Biohacker Network incentive overview
          </span>
          <h1 className="text-white text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-none mb-5" style={H}>
            Peptide memberships that pay you back —{' '}
            <span className="text-[#C7FA3E]">up to $400 in monthly credit</span> and 40% off everything.
          </h1>
          <p className="text-zinc-300 text-base leading-relaxed max-w-2xl mb-3">
            The Biohacker Network membership bundles monthly store credit, permanent member pricing, free supplies,
            a peptide education library, and access to an independent, provider-guided clinical eligibility process —
            from $99/month, cancel anytime.
          </p>
          <p className="text-zinc-500 text-sm max-w-2xl mb-7">
            New members also get a <strong className="text-zinc-300">free NinjAthlete shirt or hat</strong> and earn
            NinjAthlete Network Points on every renewal.
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            <button onClick={scrollToTiers} className="inline-flex items-center gap-2 bg-[#C7FA3E] text-black text-xs font-bold uppercase tracking-[0.15em] px-7 py-3.5 hover:bg-[#d4ff5c] transition-colors" data-testid="peptide-hero-explore-btn">
              Explore membership tiers <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <CtaButton placement="hero" urls={urls} label="Join at Biohacker Network" className="border border-white/25 text-white hover:border-[#C7FA3E] hover:text-[#C7FA3E]" testid="peptide-cta-hero" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="peptide-trust-ticker">
            {[[ShieldCheck, 'U.S.-licensed pharmacies (503A/503B)'], [Activity, 'Independent licensed providers'], [BadgeDollarSign, 'Credit worth more than the price on Pro & Elite'], [Award, 'Cancel anytime — no lock-in']].map(([Icon, t]) => (
              <div key={t} className="flex items-start gap-2 border border-white/10 bg-white/[0.03] px-3 py-2.5">
                <Icon className="w-4 h-4 text-[#C7FA3E] flex-shrink-0 mt-0.5" />
                <span className="text-zinc-400 text-[11px] leading-snug">{t}</span>
              </div>
            ))}
          </div>
          <p className="text-zinc-600 text-[11px] mt-6 max-w-3xl flex items-start gap-1.5" data-testid="peptide-hero-disclosure">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            Sponsored relationship: NinjAthlete may earn compensation when you join through links on this page. Membership is not
            medical treatment and does not guarantee a prescription — all clinical decisions are made by independent licensed providers.
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-16">
        {/* ── Value math strip ── */}
        <Section section="value_math" testid="peptide-value-math-strip">
          <div className="border border-[#00F5D4]/25 bg-[#0D0F12] p-8 sm:p-10 text-center">
            <p className="text-[#FBBF24] text-[11px] uppercase tracking-[0.35em] mb-4">{VALUE_MATH.eyebrow}</p>
            <h2 className="text-white text-4xl sm:text-5xl leading-none" style={H}>{VALUE_MATH.headline1}</h2>
            <h2 className="text-[#FBBF24] text-4xl sm:text-5xl leading-none mb-5" style={H}>{VALUE_MATH.headline2}</h2>
            <p className="text-zinc-400 text-sm max-w-2xl mx-auto mb-8">{VALUE_MATH.sub}</p>
            <div className="flex flex-col sm:flex-row items-stretch justify-center gap-2 mb-8">
              {VALUE_MATH.steps.map((s, i) => (
                <React.Fragment key={s.top}>
                  <div className={`flex-1 max-w-[220px] mx-auto sm:mx-0 border px-6 py-5 bg-[#14181E] ${s.gold ? 'border-[#F59E0B]/50 shadow-[0_0_30px_rgba(245,158,11,0.12)]' : 'border-white/10'}`} data-testid={`value-math-step-${i}`}>
                    <p className={`text-2xl sm:text-3xl font-bold ${s.gold ? 'text-[#FBBF24]' : 'text-white'}`}>{s.top}</p>
                    <p className="text-zinc-500 text-[10px] uppercase tracking-[0.25em] mt-1">{s.bottom}</p>
                  </div>
                  {i < VALUE_MATH.steps.length - 1 && <span className="self-center text-[#00F5D4] hidden sm:block"><ArrowRight className="w-5 h-5" /></span>}
                </React.Fragment>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-8">
              {VALUE_MATH.perks.map((p, i) => {
                const icons = [Wallet, Percent, Truck, Droplets, Crown];
                const Icon = icons[i % icons.length];
                return <span key={p} className="flex items-center gap-1.5 text-zinc-300 text-xs uppercase tracking-wider"><Icon className="w-3.5 h-3.5 text-[#FBBF24]" /> {p}</span>;
              })}
            </div>
            <CtaButton placement="tier_elite" urls={urls} label="Join Elite — $299/month" className="border border-[#F59E0B] text-[#FBBF24] hover:bg-[#F59E0B] hover:text-black" testid="peptide-cta-value-math" />
            <p className="text-zinc-500 text-xs mt-4">{VALUE_MATH.proLine}</p>
          </div>
        </Section>

        {/* ── Tier cards ── */}
        <Section section="tiers" eyebrow="Pick your level" title="Which membership tier fits your protocol?" testid="peptide-tiers-section">
          <div id="membership-tiers" className="grid md:grid-cols-3 gap-6 pt-4">
            {TIERS.map((t) => <TierCard key={t.key} tier={t} urls={urls} />)}
          </div>
          <p className="text-zinc-600 text-[11px] mt-6 max-w-3xl">
            Store credit applies to eligible Biohacker Network store purchases and renews monthly while your membership is active.
            Medication costs, if anything is prescribed, are always billed separately by the pharmacy. Membership does not guarantee a prescription.
          </p>
        </Section>

        {/* ── Comparison matrix ── */}
        <Section section="comparison" eyebrow="Side by side" title="Every incentive, compared" testid="peptide-comparison-section">
          <div className="border border-white/10 overflow-x-auto">
            <table className="w-full text-left min-w-[640px]" data-testid="peptide-comparison-table">
              <thead>
                <tr className="bg-[#121214] border-b border-white/10">
                  <th className="px-4 py-3.5 text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">Benefit</th>
                  <th className="px-4 py-3.5 text-white text-xs uppercase tracking-[0.2em]">Network $99</th>
                  <th className="px-4 py-3.5 text-[#00F5D4] text-xs uppercase tracking-[0.2em]">Pro $199</th>
                  <th className="px-4 py-3.5 text-[#FBBF24] text-xs uppercase tracking-[0.2em]">Elite $299</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.label} className="border-b border-white/5">
                    <td className="px-4 py-3 text-zinc-400 text-[13px]">{row.label}</td>
                    <td className="px-4 py-3 text-zinc-300 text-[13px]">{row.network}</td>
                    <td className="px-4 py-3 text-zinc-200 text-[13px] bg-[#00F5D4]/[0.03]">{row.pro}</td>
                    <td className="px-4 py-3 text-zinc-200 text-[13px] bg-[#F59E0B]/[0.03]">{row.elite}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-6 text-center">
            <CtaButton placement="footer" urls={urls} label="Compare live at Biohacker Network" className="border border-white/25 text-white hover:border-[#C7FA3E] hover:text-[#C7FA3E]" testid="peptide-cta-comparison" />
          </div>
        </Section>

        {/* ── Gift ── */}
        <Section section="gift" eyebrow="NinjAthlete exclusive" title="Complimentary NinjAthlete shirt or hat" testid="peptide-gift-section">
          <div className="border border-[#C7FA3E]/25 bg-[#C7FA3E]/[0.03] p-7 flex flex-col md:flex-row gap-6 items-start">
            <Gift className="w-8 h-8 text-[#C7FA3E] flex-shrink-0" />
            <div>
              <p className="text-zinc-300 text-sm leading-relaxed mb-3">
                Join any tier through this page and redeem one complimentary NinjAthlete performance shirt or hat ($32 retail value) —
                you pick the item, size, and color. Redemption opens after the 14-day refund period, one gift per person and household, while supplies last.
              </p>
              <p className="text-zinc-500 text-xs leading-relaxed mb-3">{PROMO_DISCLOSURE}</p>
              <Link to="/promotion-terms/biohacker-member-gift" className="text-[#C7FA3E] text-xs uppercase tracking-[0.15em] hover:underline inline-flex items-center gap-1.5" data-testid="promo-terms-link">
                Read the full promotion terms <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </Section>

        {/* ── Points ── */}
        <Section section="points" eyebrow="Loyalty that stacks" title="Earn NinjAthlete Network Points on every renewal" testid="peptide-points-section">
          <div className="border border-white/10 bg-[#121214] p-7 space-y-3">
            <p className="text-zinc-300 text-sm leading-relaxed">
              Every membership renewal earns NinjAthlete Network Points — Elite earns the highest rate. Points redeem for
              NinjAthlete merchandise: 100 points = $1 of eligible merch value, 1,000-point minimum, $50 per-order maximum.
            </p>
            <ul className="space-y-1.5 text-zinc-500 text-xs">
              <li>• Points never apply to medical services or prescriptions and cannot be exchanged for cash</li>
              <li>• Points are never awarded based on prescription quantity, dosage, compound selection, treatment selection, or medication spend</li>
            </ul>
            <Link to="/rewards/wallet" className="inline-flex items-center gap-1.5 text-[#C7FA3E] text-xs uppercase tracking-[0.15em] hover:underline" data-testid="wallet-link">
              <Wallet className="w-3.5 h-3.5" /> Already a member? Open your points wallet →
            </Link>
          </div>
        </Section>

        {/* ── Education hub ── */}
        <Section section="what_are_peptides" eyebrow="The knowledge base" title="What are peptides?" testid="peptide-what-section">
          <div className="max-w-3xl space-y-4 text-zinc-400 text-sm leading-relaxed">
            <p>
              Peptides are short chains of amino acids — typically 2 to 50 — that act as precision signaling molecules in the body.
              Where proteins are the body&apos;s building materials, peptides are closer to its messaging system: they tell cells when to
              repair tissue, release hormones, modulate inflammation, or regulate metabolism.
            </p>
            <p>
              That signaling role is why peptides have become one of the most researched frontiers in performance, recovery, and longevity
              science. Some peptides are FDA-approved medications for specific indications; many others are compounded or investigational,
              with evidence that ranges from robust clinical trials to early preclinical work. Understanding that spectrum — and getting
              guidance from a licensed provider rather than an anonymous online vendor — is the entire reason provider-guided memberships exist.
            </p>
          </div>
        </Section>

        <Section section="how_access" eyebrow="Do it right" title="Why provider-guided access beats gray-market vials" testid="peptide-how-section">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-red-500/20 bg-red-500/[0.03] p-6">
              <p className="text-red-400 text-xs uppercase tracking-[0.2em] mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Unregulated "research" vendors</p>
              <ul className="space-y-2 text-zinc-400 text-[13px]">
                <li>• No identity, purity, or sterility verification</li>
                <li>• No dosing guidance or clinical monitoring</li>
                <li>• No recourse when something goes wrong</li>
                <li>• Products labeled "not for human use" for a reason</li>
              </ul>
            </div>
            <div className="border border-[#C7FA3E]/25 bg-[#C7FA3E]/[0.03] p-6">
              <p className="text-[#C7FA3E] text-xs uppercase tracking-[0.2em] mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Provider-guided membership route</p>
              <ul className="space-y-2 text-zinc-400 text-[13px]">
                <li>• Evaluation by an independent licensed provider</li>
                <li>• Dispensing only by U.S. state-licensed pharmacies</li>
                <li>• Documented sourcing, handling, and cold-chain shipping</li>
                <li>• Ongoing education and protocol tracking tools</li>
              </ul>
            </div>
          </div>
        </Section>

        <Section section="process" eyebrow="Three steps" title="How the eligibility process works" testid="peptide-process-section">
          <ol className="grid md:grid-cols-3 gap-4">
            {[
              ['1. Secure intake', 'Complete a private health questionnaire on the Biohacker Network platform. NinjAthlete never sees any of it.'],
              ['2. Provider evaluation', 'An independent licensed provider reviews your history and goals in a telehealth evaluation and makes all clinical decisions.'],
              ['3. Licensed fulfillment', 'If — and only if — something is clinically appropriate, a U.S. state-licensed pharmacy fills and ships it with proper handling.'],
            ].map(([t, d]) => (
              <li key={t} className="border border-white/10 bg-[#121214] p-6">
                <p className="text-white text-sm font-bold mb-2">{t}</p>
                <p className="text-zinc-500 text-[13px] leading-relaxed">{d}</p>
              </li>
            ))}
          </ol>
          <p className="text-zinc-600 text-[11px] mt-4">Membership does not guarantee a consultation outcome, prescription, medication, dosage, or treatment plan. Some applicants will not qualify.</p>
        </Section>

        <Section section="categories" eyebrow="What members study" title="Peptide categories covered in the member library" testid="peptide-categories-section">
          <div className="grid md:grid-cols-2 gap-4">
            {PEPTIDE_CATEGORIES.map((c) => (
              <div key={c.name} className="border border-white/10 bg-[#121214] p-6" data-testid={`peptide-category-${PEPTIDE_CATEGORIES.indexOf(c)}`}>
                <p className="text-white text-sm font-bold flex items-center gap-2"><FlaskConical className="w-4 h-4 text-[#00F5D4]" /> {c.name}</p>
                <p className="text-[#00F5D4] text-[11px] uppercase tracking-wider mt-1 mb-2">{c.examples}</p>
                <p className="text-zinc-500 text-[13px] leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
          <p className="text-zinc-600 text-[11px] mt-4 max-w-3xl">
            Educational overview only — nothing here is medical advice, an efficacy claim, or a promise of availability. Whether any
            compound is appropriate (or prescribable) for you is decided solely by an independent licensed provider.
            Want the deep dive? See the free <Link to="/peptide-cheat-sheet" className="text-[#C7FA3E] hover:underline">NinjAthlete peptide cheat sheet</Link>.
          </p>
        </Section>

        <Section section="fda" eyebrow="Know the difference" title="FDA-approved vs. compounded medication" testid="peptide-fda-section">
          <div className="max-w-3xl space-y-4 text-zinc-400 text-sm leading-relaxed">
            <p>
              <strong className="text-zinc-200">FDA-approved medications</strong> have completed clinical trials and FDA review for
              specific indications. <strong className="text-zinc-200">Compounded medications</strong> are prepared by licensed compounding
              pharmacies for individual patients — they are legal when properly prescribed and dispensed, but they are{' '}
              <strong className="text-zinc-200">not FDA-approved</strong>, and the FDA does not review compounded drugs for safety,
              effectiveness, or quality before they are marketed.
            </p>
            <p>
              Many peptides discussed in performance communities exist only in compounded or investigational form. A responsible program
              is transparent about that distinction — your provider explains the regulatory status of anything they prescribe, and you
              can verify pharmacy licensing through your state board of pharmacy.
            </p>
          </div>
        </Section>

        <Section section="standards" eyebrow="Quality bar" title="Provider & pharmacy standards" testid="peptide-standards-section">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              ['Licensed providers', 'Clinical evaluations are performed by independent, state-licensed providers operating under telehealth regulations.'],
              ['503A / 503B pharmacies', 'Dispensing is limited to U.S. state-licensed compounding pharmacies operating under sections 503A/503B of the FD&C Act.'],
              ['Secure health data', 'Intake and clinical records stay on Biohacker Network\u2019s secure platform — never shared with NinjAthlete.'],
              ['Documented handling', 'Temperature-controlled shipping and documented chain of custody for anything a pharmacy dispenses.'],
            ].map(([t, d]) => (
              <div key={t} className="border border-white/10 bg-[#121214] p-5">
                <p className="text-white text-sm font-bold mb-1.5">{t}</p>
                <p className="text-zinc-500 text-[13px] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section section="costs" eyebrow="No surprises" title="Costs, cancellation & pausing" testid="peptide-costs-section">
          <div className="max-w-3xl space-y-3 text-zinc-400 text-sm leading-relaxed">
            <p>Membership is billed monthly at $99, $199, or $299 by tier. Cancel anytime from the billing portal — cancellation takes effect at the end of the current billing period, with no cancellation fees. Annual plans can be paused.</p>
            <p>Store credit and member pricing apply to eligible store purchases. Any prescribed medication is always billed separately by the dispensing pharmacy and depends entirely on your clinical plan — membership fees never include, discount claims about, or guarantee medication.</p>
          </div>
        </Section>

        <Section section="risks" eyebrow="Read this" title="Risks, limitations & adverse events" testid="peptide-risks-section">
          <div className="border border-amber-500/25 bg-amber-500/[0.03] p-6 max-w-3xl">
            <p className="text-zinc-400 text-[13px] leading-relaxed mb-3">
              All medications — FDA-approved or compounded — carry risk. Compounded products are not FDA-reviewed before marketing.
              Research on many peptides is early-stage, and marketing claims elsewhere online frequently outrun the evidence. Never start,
              stop, or combine anything without guidance from your licensed provider, and disclose your full health history during intake.
            </p>
            <p className="text-zinc-500 text-[12px] leading-relaxed">
              Report suspected adverse events to your provider and to FDA MedWatch (fda.gov/medwatch or 1-800-FDA-1088).
            </p>
          </div>
        </Section>

        {/* ── Relationship disclosure ── */}
        <Section section="relationship" eyebrow="Full transparency" title="Our relationship with Biohacker Network" testid="peptide-relationship-section">
          <div className="border border-white/10 bg-[#121214] p-6 max-w-3xl space-y-3">
            <p className="text-zinc-400 text-sm leading-relaxed">
              NinjAthlete and Biohacker Network are separate companies with a commercial relationship: NinjAthlete may earn compensation
              when readers join through links on this page, and the two companies share the attribution data necessary to administer the
              merchandise promotion — never sensitive medical information.
            </p>
            <p className="text-zinc-500 text-[13px] leading-relaxed">{DISCLOSURE}</p>
          </div>
        </Section>

        {/* ── FAQ ── */}
        <Section section="faq" eyebrow="Everything answered" title="Peptide membership FAQ" testid="peptide-faq-section">
          <div className="space-y-3 max-w-3xl" data-testid="peptide-faq-accordion">
            {FAQS.map((f, i) => (
              <FaqItem key={i} f={f} i={i} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </Section>

        {/* ── Author / review ── */}
        <Section section="author" eyebrow="E-E-A-T" title="Author & medical review" testid="peptide-author-section">
          <div className="border border-white/10 bg-[#121214] p-6 max-w-3xl">
            <p className="text-zinc-400 text-[13px] leading-relaxed">
              Written by the <strong className="text-zinc-200">NinjAthlete Editorial Team</strong> · Published August 21, 2026 · Updated August 22, 2026.
              This page is scheduled for review by a qualified, licensed medical reviewer; the reviewer&apos;s name and credentials will be published here upon completion.
              Content is educational and is not medical advice.
            </p>
          </div>
        </Section>

        {/* ── References ── */}
        <Section section="references" eyebrow="Primary sources" title="References" testid="peptide-references-section">
          <ul className="space-y-2 max-w-3xl text-[13px]">
            {[
              ['FDA — Human Drug Compounding (503A/503B overview)', 'https://www.fda.gov/drugs/guidance-compliance-regulatory-information/human-drug-compounding'],
              ['FDA — Compounding and the FDA: Questions and Answers', 'https://www.fda.gov/drugs/human-drug-compounding/compounding-and-fda-questions-and-answers'],
              ['FDA MedWatch — Report an adverse event', 'https://www.fda.gov/safety/medwatch-fda-safety-information-and-adverse-event-reporting-program'],
              ['NIH / PubMed — Therapeutic peptides: current applications and future directions', 'https://pubmed.ncbi.nlm.nih.gov/35165272/'],
              ['ClinicalTrials.gov — Registered peptide studies', 'https://clinicaltrials.gov/'],
              ['FTC — Endorsement Guides (disclosure requirements)', 'https://www.ftc.gov/business-guidance/resources/ftcs-endorsement-guides-what-people-are-asking'],
            ].map(([label, href]) => (
              <li key={href}>
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-[#C7FA3E] inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3 h-3 flex-shrink-0" /> {label}
                </a>
              </li>
            ))}
          </ul>
        </Section>

        {/* ── Final CTA ── */}
        <section data-section="final_cta" className="border border-[#F59E0B]/30 bg-gradient-to-b from-[#1F1910] to-[#0E0C09] p-10 text-center" data-testid="peptide-final-cta-section">
          <Zap className="w-7 h-7 text-[#FBBF24] mx-auto mb-4" />
          <h2 className="text-white text-3xl sm:text-4xl mb-3" style={H}>Ready to stop overpaying for your protocol?</h2>
          <p className="text-zinc-400 text-sm max-w-xl mx-auto mb-7">
            Pick a tier, get more back in credit and savings than you pay on Pro and Elite, and claim your free NinjAthlete gear.
            Cancel anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <CtaButton placement="footer" urls={urls} label="View membership options" className="bg-[#C7FA3E] text-black font-bold hover:bg-[#d4ff5c]" testid="peptide-cta-final" />
            <CtaButton placement="tier_elite" urls={urls} label="Join Elite — $299/mo" className="border border-[#F59E0B] text-[#FBBF24] hover:bg-[#F59E0B] hover:text-black" testid="peptide-cta-final-elite" />
          </div>
          <p className="text-zinc-600 text-[11px] mt-6">{DISCLOSURE}</p>
        </section>
      </div>

      {/* ── Sticky mobile CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-white/10 bg-black/85 backdrop-blur-xl px-4 py-3 flex items-center justify-between gap-3" data-testid="peptide-sticky-cta">
        <div>
          <p className="text-white text-xs font-bold">Memberships from $99/mo</p>
          <p className="text-zinc-500 text-[10px]">Up to $400/mo credit + 40% off</p>
        </div>
        <CtaButton placement="sticky_mobile" urls={urls} label="Join now" className="bg-[#C7FA3E] text-black font-bold px-5 py-2.5" testid="peptide-cta-sticky" />
      </div>
    </div>
  );
}
